import { EyeOutlined, PrinterOutlined, SendOutlined } from "@ant-design/icons";
import { Button, Col, DatePicker, Form, Input, Modal, Row, message } from "antd";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import SockJS from "sockjs-client";
import Stomp from "stompjs";
import { API_BASE_URL } from "../../config/serverApiConfig";
import LabPreview from "../matter/LabPreview";
import { labPayloadFromChecks, selectedTestCount, TEST_FIELDS, typesFromChecks } from "../../pages/labTests";
import "../matter/LabPreview.css";

function connectStomp() {
  return new Promise((resolve, reject) => {
    const socket = new SockJS(`${API_BASE_URL}socket`);
    const stomp = Stomp.over(socket);
    stomp.debug = () => {};
    stomp.connect({}, () => resolve(stomp), (err) => reject(err));
  });
}

function PatientCliche({ patientId, name, age }) {
  const [form] = Form.useForm();
  const [checks, setChecks] = useState({});
  const [sending, setSending] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const stompRef = useRef(null);
  const date = Form.useWatch("date", form);
  const discount = Form.useWatch("discount", form);

  useEffect(() => {
    return () => {
      if (stompRef.current?.connected) stompRef.current.disconnect();
    };
  }, []);

  const toggleTest = (field) => {
    setChecks((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const sendToLab = async () => {
    if (!patientId) return;
    if (!selectedTestCount(checks)) {
      message.warning("تکایە پشکنینێک هەڵبژێرە");
      return;
    }
    const values = form.getFieldsValue();
    setSending(true);
    const data = {
      ...labPayloadFromChecks(checks),
      date: dayjs(values.date || dayjs()).format("YYYY-MM-DD"),
      discount: values.discount ?? 40,
      p_id: patientId,
    };
    try {
      if (!stompRef.current?.connected) {
        stompRef.current = await connectStomp();
      }
      stompRef.current.send("/app/sendMessage", {}, JSON.stringify(data));
      setChecks({});
      form.setFieldsValue({ date: dayjs(), discount: 40 });
      message.success("کڵێشە نێردرا بۆ تاقیگە");
    } catch {
      message.error("نەتوانرا بنێردرێت بۆ تاقیگە");
    } finally {
      setSending(false);
    }
  };

  const downloadPdf = async () => {
    setPrintLoading(true);
    try {
      const [{ pdf }, { LabPdfDocument }] = await Promise.all([import("@react-pdf/renderer"), import("../matter/LabPdfDocument")]);
      const blob = await pdf(
        <LabPdfDocument
          type={typesFromChecks(checks)}
          other={{
            name: name || "",
            date,
            age: age || "",
            discount,
          }}
        />
      ).toBlob();
      window.open(URL.createObjectURL(blob), "_blank", "noopener,noreferrer");
    } catch {
      message.error("نەتوانرا PDF دروست بکرێت");
    } finally {
      setPrintLoading(false);
    }
  };

  return (
    <div className="patient-cliche">
      <div className="cliche-known">
        <div>
          <small>ناوی تەواو</small>
          <strong>{name || "—"}</strong>
        </div>
        <div>
          <small>ژمارە</small>
          <strong>{patientId ? `ID-00${patientId}` : "—"}</strong>
        </div>
        <div>
          <small>تەمەن</small>
          <strong>{age ? `${age} ساڵ` : "—"}</strong>
        </div>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={11}>
          <div className="lab-form-card">
            <Form form={form} layout="vertical" initialValues={{ date: dayjs(), discount: 40 }}>
              <Row gutter={12}>
                <Col span={14}>
                  <Form.Item name="date" label="بەروار">
                    <DatePicker style={{ width: "100%" }} size="large" />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item name="discount" label="داشکاندن %">
                    <Input size="large" />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
            <div className="lab-chip-grid">
              {TEST_FIELDS.map((field) => (
                <button key={field.form} type="button" className={`lab-chip ${checks[field.form] ? "is-on" : ""}`} onClick={() => toggleTest(field.form)}>
                  <span className="lab-chip-box">{checks[field.form] ? "✓" : ""}</span>
                  {field.label}
                </button>
              ))}
            </div>
            <div className="lab-actions" style={{ marginTop: 16 }}>
              <Button className="cliche-preview-open" size="large" icon={<EyeOutlined />} onClick={() => setPreviewOpen(true)}>
                پێشبینینی کڵێشە
              </Button>
              <Button type="primary" size="large" icon={<SendOutlined />} loading={sending} onClick={sendToLab}>
                ناردن بۆ تاقیگە
              </Button>
              <Button size="large" icon={<PrinterOutlined />} loading={printLoading} onClick={downloadPdf}>
                پرێنت / PDF
              </Button>
            </div>
          </div>
        </Col>
        <Col className="cliche-preview-col" xs={24} xl={13}>
          <div className="lab-preview-card">
            <div className="lab-preview-toolbar">
              <strong>پێشبینینی کڵێشە</strong>
              <span>{selectedTestCount(checks)} پشکنین</span>
            </div>
            <div className="lab-preview-canvas">
              <LabPreview patientName={name} date={date} age={age} discount={discount} checks={checks} />
            </div>
          </div>
        </Col>
      </Row>
      <Modal className="cliche-preview-modal" title="پێشبینینی کڵێشە" open={previewOpen} footer={null} centered onCancel={() => setPreviewOpen(false)} width={480}>
        <LabPreview patientName={name} date={date} age={age} discount={discount} checks={checks} />
      </Modal>
    </div>
  );
}

export default PatientCliche;
